import React from 'react';

interface HelpContent {
  title: string;
  icon?: string;
  description: string;
  details?: string[];
  benchmarkTable?: {
    title: string;
    headers: string[];
    rows: (string | number)[][];
  };
  calculation?: {
    formula: string;
    example: string;
    factors?: string[];
  };
  tips?: string[];
  currentValue?: {
    value: string | number;
    explanation: string;
  };
}

interface FieldHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  content: HelpContent | null;
  industryName?: string;
}

export const FieldHelpModal: React.FC<FieldHelpModalProps> = ({
  isOpen,
  onClose,
  content,
  industryName
}) => {
  if (!isOpen || !content) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
          <div className="relative bg-white rounded-lg text-left shadow-xl transform transition-all sm:my-8 sm:max-w-2xl sm:w-full">
            {/* Header */}
            <div className="bg-gradient-to-r from-sky-50 to-blue-50 px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{content.icon || '📊'}</span>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {content.title}
                  </h3>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
              {/* Description */}
              <div className="mb-4">
                <h4 className="font-semibold text-gray-800 mb-2">What it means:</h4>
                <p className="text-gray-600">{content.description}</p>
              </div>

              {/* Details */}
              {content.details && content.details.length > 0 && (
                <div className="mb-4">
                  <ul className="list-disc list-inside space-y-1 text-gray-600">
                    {content.details.map((detail, index) => (
                      <li key={index}>{detail}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Benchmark Table */}
              {content.benchmarkTable && (
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-800 mb-2">
                    {content.benchmarkTable.title}
                    {industryName && ` - ${industryName}`}
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full border border-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          {content.benchmarkTable.headers.map((header, index) => (
                            <th key={index} className="px-4 py-2 text-left text-sm font-medium text-gray-700 border-b">
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {content.benchmarkTable.rows.map((row, rowIndex) => (
                          <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            {row.map((cell, cellIndex) => (
                              <td key={cellIndex} className="px-4 py-2 text-sm text-gray-600 border-b">
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Calculation */}
              {content.calculation && (
                <div className="mb-4 bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">How to calculate:</h4>
                  <div className="space-y-2">
                    <div className="font-mono text-sm bg-white p-2 rounded border border-blue-200">
                      {content.calculation.formula}
                    </div>
                    <div className="text-sm text-gray-600">
                      <strong>Example:</strong> {content.calculation.example}
                    </div>
                    {content.calculation.factors && (
                      <div className="text-sm text-gray-600">
                        <strong>Factors to consider:</strong>
                        <ul className="list-disc list-inside mt-1">
                          {content.calculation.factors.map((factor, index) => (
                            <li key={index}>{factor}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Tips */}
              {content.tips && content.tips.length > 0 && (
                <div className="mb-4 bg-amber-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                    <span>💡</span> Tips
                  </h4>
                  <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                    {content.tips.map((tip, index) => (
                      <li key={index}>{tip}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Current Value */}
              {content.currentValue && (
                <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Your current value:</span>
                    <span className="text-lg font-bold text-green-700">{content.currentValue.value}</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">{content.currentValue.explanation}</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-3 flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-sky-600 text-white text-sm font-medium rounded-md hover:bg-sky-700 transition-colors"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};